<script setup lang="ts">
import WorkOrderSelect from '@/components/rc/common/RcWorkOrderSelect.vue'
import { resetForm } from '@/helpers/ep/form'
import { validateTrackingInformationGroup } from '@/helpers/ep/form-validators'
import { disabledDate } from '@/helpers/ep/table'
import { ContainerTypeSelectOptions } from '@/types/rc/containers'
import type { IShipmentFormOutbound } from '@/types/rc/forms'
import { Icon } from '@iconify/vue'
import { ElMessage, type FormInstance, type FormRules, type UploadFile } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { DateTime } from 'luxon'
import { vMaska } from 'maska/vue'
import { reactive, ref, toRefs } from 'vue'

const outboundForm = defineModel<IShipmentFormOutbound>({ required: true })
const props = withDefaults(
  defineProps<{ carrierOptions: OptionType[]; disabled?: boolean; lockWorkOrder?: boolean }>(),
  {
    lockWorkOrder: false,
  }
)
const { carrierOptions, disabled } = toRefs(props)

const containerTypes = ref(ContainerTypeSelectOptions)

// Accepted file types for receipts
const acceptedFileTypes = ['application/pdf', 'image/jpeg', 'image/png']

const outboundFormRef = ref<FormInstance>()

// Outbound form validation rules.
const outboundFormRules = reactive<FormRules<IShipmentFormOutbound>>({
  work_order: [{ required: true, message: 'Work order is required.', trigger: 'change' }],
  tracking_information: [
    {
      type: 'object',
      validator: validateTrackingInformationGroup,
      fields: {
        tracking_number: { type: 'string' },
        bol_number: { type: 'string' },
        waybill_number: { type: 'string' },
      },
    },
  ],
  delivered_date: [
    {
      validator: (rule: unknown, value: string, callback: (error?: Error | string) => void) => {
        // If customer_pick_up flag is set, check delivered date is set as well
        if (outboundForm.value.customer_pick_up && !outboundForm.value.delivered_date) {
          return callback('Delivered Date must be set for customer Pick Up')
        }

        // If picked up date is set, check the delivered date is equal or bigger
        if (!outboundForm.value.picked_up_date) {
          return callback()
        }

        if (DateTime.fromISO(outboundForm.value.picked_up_date) > DateTime.fromISO(value)) {
          return callback(new Error('Delivered Date must be equal or bigger than Picked Up Date'))
        }

        callback()
      },
      trigger: ['blur', 'submit'],
    },
  ],
})

/**
 * Runs validation on an inbound or outbound tracking information form group.
 *
 * @param {FormInstance} formEl the (inbound or outbound) form instance to validate.
 */
const validateTrackingInformation = (formEl: FormInstance | undefined) => {
  if (!formEl) return
  formEl.validateField('tracking_information', () => undefined)
}

/**
 * Validate form, return validation state.
 *
 * @throws {Error} on validation error.
 */
const isValid = async () => {
  try {
    const res = await outboundFormRef.value?.validate()
    return res // true
  } catch {
    return false
  }
}

/**
 * Handle receipt file change
 */
const handleReceiptFileChange = (uploadFile: UploadFile, uploadFiles: UploadFile[]) => {
  if (uploadFile.raw) {
    // Validate file type and size
    const isValidType = acceptedFileTypes.includes(uploadFile.raw.type)
    const isValidSize = uploadFile.raw.size <= 10 * 1024 * 1024 // 10MB

    if (!isValidType) {
      ElMessage.error('File must be a PDF, JPG or PNG file')
      return
    }

    if (!isValidSize) {
      ElMessage.error('File size must be less than 10MB')
      return
    }
  }

  // Update the receipts array based on the file list
  outboundForm.value.receipts = [...uploadFiles]
}

/**
 * Handle receipt file removal
 */
const handleReceiptFileRemove = (uploadFile: UploadFile, uploadFiles: UploadFile[]) => {
  // Update the receipts array based on the file list
  outboundForm.value.receipts = [...uploadFiles]

  // Add the deleted receipt ID to the array (this will only be used for updates)
  if (outboundForm.value.deleted_receipt_ids) {
    outboundForm.value.deleted_receipt_ids?.push(uploadFile.uid)
  } else {
    outboundForm.value.deleted_receipt_ids = [uploadFile.uid]
  }
}

const handleReceiptFileExceed = () => {
  ElMessage.warning('Maximum 5 files allowed')
}

/**
 * Handles form reset.
 */
const reset = () => {
  resetForm(outboundFormRef.value)
  outboundForm.value.receipts = []
}

defineExpose({
  isValid,
  reset,
})
</script>

<template>
  <el-form
    ref="outboundFormRef"
    class="form"
    :model="outboundForm"
    :rules="outboundFormRules"
    label-position="left"
    label-width="160px"
    hide-required-asterisk
    :disabled="disabled"
    scroll-to-error
    :scroll-into-view-options="{ behavior: 'smooth', block: 'start' }"
  >
    <el-form-item class="form-group" label="General">
      <el-form-item label="Work Order Number" prop="work_order">
        <WorkOrderSelect
          v-model="outboundForm.work_order"
          placeholder="Search for a work order"
          :disabled="lockWorkOrder"
        />
      </el-form-item>
    </el-form-item>
    <el-form-item label="Customer Pick Up" prop="customer_pick_up">
      <el-switch v-model="outboundForm.customer_pick_up" />
    </el-form-item>
    <el-form-item v-if="!outboundForm.customer_pick_up" class="form-group">
      <template #label>
        <rl-form-group-label label="Tracking Information" :optional="!outboundForm.tracking_information.carrier" />
      </template>
      <div class="form-group__items">
        <el-form-item label="Carrier" prop="tracking_information.carrier">
          <el-select-v2
            v-model="outboundForm.tracking_information.carrier"
            :options="carrierOptions"
            placeholder="Select carrier..."
            clearable
            filterable
            allow-create
            @clear="validateTrackingInformation(outboundFormRef)"
          />
        </el-form-item>
        <el-form-item prop="tracking_information">
          <el-collapse-transition>
            <div v-show="outboundForm.tracking_information.carrier" class="form-group__items tracking-information">
              <el-form-item label="Tracking Number" prop="tracking_information.tracking_number">
                <el-input
                  v-model="outboundForm.tracking_information.tracking_number"
                  placeholder="e.g. 9999 9999 9999 9999"
                  clearable
                  @input="validateTrackingInformation(outboundFormRef)"
                />
              </el-form-item>
              <el-form-item label="BOL Number" prop="tracking_information.bol_number">
                <el-input
                  v-model="outboundForm.tracking_information.bol_number"
                  placeholder="e.g. AA999999999"
                  clearable
                  @input="validateTrackingInformation(outboundFormRef)"
                />
              </el-form-item>
              <el-form-item label="Waybill Number" prop="tracking_information.waybill_number">
                <el-input
                  v-model="outboundForm.tracking_information.waybill_number"
                  placeholder="e.g. 99 9999 9999"
                  clearable
                  @input="validateTrackingInformation(outboundFormRef)"
                />
              </el-form-item>
            </div>
          </el-collapse-transition>
        </el-form-item>
      </div>
    </el-form-item>
    <el-form-item class="form-group expected">
      <template #label>
        <rl-form-group-label label="Expected Content" :optional="true" />
      </template>
      <div class="form-group__items--grid">
        <el-form-item label="Container Types" prop="expected_container_types">
          <el-select-v2
            v-model="outboundForm.expected_container_types"
            :options="containerTypes"
            multiple
            placeholder="Select container types..."
            clearable
          />
        </el-form-item>
        <el-form-item label="Expected Containers" prop="expected_containers">
          <el-input
            v-model="outboundForm.expected_containers"
            v-maska
            data-maska="9,99#"
            data-maska-tokens="9:[0-9]:repeated"
            data-maska-reversed
            clearable
          />
        </el-form-item>
        <el-form-item label="Expected Miners" prop="expected_miners">
          <el-input
            v-model="outboundForm.expected_miners"
            v-maska
            data-maska="9,99#"
            data-maska-tokens="9:[0-9]:repeated"
            data-maska-reversed
            clearable
          />
        </el-form-item>
      </div>
    </el-form-item>
    <template v-if="!outboundForm.customer_pick_up">
      <el-form-item prop="receipts">
        <template #label>
          <rl-form-group-label label="Receipts" :optional="true" />
        </template>
        <div class="w-full">
          <el-upload
            v-model:file-list="outboundForm.receipts"
            class="w-full"
            :on-change="handleReceiptFileChange"
            :on-remove="handleReceiptFileRemove"
            :on-exceed="handleReceiptFileExceed"
            :auto-upload="false"
            :limit="5"
            multiple
            :accept="acceptedFileTypes.join(', ')"
            drag
            :disabled="disabled"
          >
            <el-icon class="el-icon--upload">
              <icon icon="material-symbols:upload-rounded" :height="48" />
            </el-icon>

            <div>
              <p><span style="color: var(--text-brand)">Upload a file</span> or drag and drop</p>
              <small>PDF, JPG, PNG files up to 10MB (Max 5 files)</small>
            </div>
          </el-upload>
        </div>
      </el-form-item>
      <el-form-item prop="picked_up_date">
        <template #label>
          <rl-form-group-label label="Picked Up Date" :optional="true" />
        </template>
        <div class="form-group__items--grid">
          <el-date-picker
            v-model="outboundForm.picked_up_date"
            style="width: 100%"
            value-format="YYYY-MM-DDThh:mm:ssZ"
            :disabled-date="disabledDate"
            placeholder="Select a date..."
          />
        </div>
      </el-form-item>
    </template>
    <el-form-item prop="delivered_date">
      <template #label>
        <rl-form-group-label label="Delivered Date" :optional="true" />
      </template>
      <div class="form-group__items--grid">
        <el-date-picker
          v-model="outboundForm.delivered_date"
          style="width: 100%"
          value-format="YYYY-MM-DDThh:mm:ssZ"
          :disabled-date="disabledDate"
          placeholder="Select a date..."
        />
      </div>
    </el-form-item>
  </el-form>
</template>

<style scoped lang="scss">
.form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-xl);

  .upload {
    width: 100%;
  }

  &-group {
    width: 100%;
    display: inline-flex;

    &__items {
      width: 100%;
      display: flex;
      flex-direction: column;

      &--grid {
        width: 100%;
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: auto;
        gap: var(--spacing-spacing-md) var(--spacing-spacing-xs);
      }
    }

    .el-form-item {
      width: 100%;
      display: flex;
      flex-direction: column;
    }
  }
}

.tracking-information {
  padding-top: var(--spacing-spacing-md);
}

.remote-search--loading {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.damages,
.containers,
.container-numbers {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-spacing-md);
}

.damages-item {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-md);
}

.damages-item,
.containers-item,
.container-number-item {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-md);

  &__header {
    min-height: var(--spacing-spacing-lg);
    width: 100%;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
  }
}

.damages-item,
.containers-item {
  align-items: center;
}

.container-number-item {
  align-items: flex-start;
  gap: var(--spacing-spacing-xs);

  &__controls {
    width: 100%;
    display: inline-flex;
    gap: var(--spacing-spacing-xs);
    align-items: center;
  }
}

:deep(.el-form-item__content) {
  line-height: 1;
}

:deep(.el-upload-dragger) {
  height: 140px;
  width: 100%;
  padding: 0;
  line-height: 1.3em;

  .el-icon {
    margin: 0;
    width: 56px;
  }
}

:deep(.el-upload-list) {
  margin-top: var(--spacing-spacing-sm);
}

:deep(.el-upload-list__item) {
  background-color: var(--bg-color-light);
  border: 1px solid var(--border-color-light);
  border-radius: var(--border-radius-small);
}
</style>
